// ─────────────────────────────────────────────
//  cellReader — ONE reader for one cell of the daily sheet.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  ⚠️ THERE USED TO BE TWO. `parseWesleyChapel` (496 lines, the retired
//  5-column layout) and `parsePrelimAssignments` (the 6-column layout that
//  actually runs) both claimed to read this sheet, and no production path
//  called the first — while the second imported its cell helpers. They had
//  already drifted: the dead one never wrote `crnaToRoom`/`crnaFromRoom`, so
//  the sheet's own routing arrows were a dead binding on that path, and it
//  dropped a timeless row carrying a CRNA where the live one benched them.
//  Two readers is two answers to "who is in this room". This is the one.
//
//  ⚠️ AND IT FAILS CLOSED. "How many people are in this cell" is a SINGLE
//  decision made in `splitCell`, and every spelling the sheet uses — `orient`,
//  a comma, a slash, a trailing parenthesis — goes through the same
//  person-shaped test in domain/personSegment. A shape this reader has not
//  been shown becomes a `review` item naming the verbatim cell, never a guess
//  and never a new colleague.
//
//  ⚠️ THE READER SAYS WHAT IT DID NOT UNDERSTAND. `readProviderCell` returns
//  `people`, `notes` (understood, but not a person) and `review` (not
//  understood). The unattended nightly import has to put a refusal somewhere a
//  human sees it, because it FULL-RESETS each board — a row that quietly falls
//  out does not merely fail to be added, the day is rebuilt without it.
//
//  Pure function — no network, no Firebase. Input is raw sheet text.
// ─────────────────────────────────────────────

import { orientationParts } from "../../domain/orientation";
import {
  coProviderIssue,
  coProviderSegments,
  stripTimeNote,
} from "../../domain/providerCell";
import {
  flipSurnameFirst,
  isEmptySegment,
  isPersonSegment,
  isShiftToken,
  normalizeSegment,
  personSegmentIssue,
  stripRoutingNote,
} from "../../domain/personSegment";

const norm = (v) => String(v ?? "").trim();

/** "xxxx" / "TBD" / "?????" / "--" → this seat names nobody. */
export const isPlaceholderCell = (s) => isEmptySegment(norm(s));

// Wesley Chapel room families on the live sheet:
//   WOR<n> / "WOR <n>" — the Wesley Chapel ORs
//   "WC <label>"       — WC ancillary areas: WC CL, WC MRI, WC OB, WC E1-3, WC TBD, WC IR/CT/TEE
// NOT Wesley Chapel: "WC" immediately followed by a DIGIT (WC1, WC2, WC5, WC7…)
// — those are TAMPA Women's Center rooms (user-confirmed 2026-06-23, and
// confirmed again 2026-08-20 against the department's own `Room Defs` tab).
// "WORLD" must not match (WOR + a letter is not an OR).
export const WC_ROOM_RE = /^(wor(?=\d|\s|$)|wc(?=\s))/i;

// ─── Clock ──────────────────────────────────────────────────────────────────

/**
 * Normalize a clock token to military forms.
 * "0700"/"700"/"7:00"/"07:00"/"700.0" → { military:"0700", hhmm:"07:00" }; null if bad.
 *
 * ⚠️ THE ".0" IS REAL. Google Sheets stores some Duration cells as NUMBERS, so
 * the export prints "700.0" where the sheet shows "700". Three rows read that
 * way on 2026-08-20's earlier edition.
 */
export function normalizeTime(token) {
  const t = norm(token).replace(/\.0+$/, "");
  if (!t) return null;
  const m = t.match(/^(\d{1,2}):?(\d{2})$/) || t.match(/^(\d{1,2})()$/);
  if (!m) return null;
  const hh = String(m[1]).padStart(2, "0");
  const mm = (m[2] || "00").padStart(2, "0");
  if (Number(hh) > 23 || Number(mm) > 59) return null;
  return { military: `${hh}${mm}`, hhmm: `${hh}:${mm}` };
}

const clockTokens = (s) => norm(s).split(/[\s/]+/).filter(Boolean);
const firstClock = (s) => {
  for (const t of clockTokens(s)) {
    const n = normalizeTime(t);
    if (n) return n;
  }
  return null;
};
const lastClock = (s) => {
  let out = null;
  for (const t of clockTokens(s)) {
    const n = normalizeTime(t);
    if (n) out = n;
  }
  return out;
};

/**
 * "START-END" → { start, end } normalized (each may be null).
 *
 * ⚠️ A DURATION CELL IS NOT ALWAYS ONE RANGE. `WC E1` carried
 * "0715 0815/1330- 1730" on 2026-08-20 — two blocks, the first written with no
 * dash. Splitting on the first dash gave `start: null`, so the row was kept
 * with an end time and no start. Taking the FIRST clock-shaped token before
 * the dash and the LAST after it recovers 07:15 → 17:30, which is the window
 * the room is actually occupied.
 */
export function parseTimeRange(cell) {
  const parts = norm(cell).split(/\s*[-–—]\s*/);
  return { start: firstClock(parts[0]), end: lastClock(parts.slice(1).join(" ")) };
}

// ─── Attending (MD) column ──────────────────────────────────────────────────

/**
 * "OSU (12) AIC" / "MAH" / "SAN (3/4)" → { code, callOrder, postCall, isAIC }.
 * callOrder = first integer in parentheses (or first bare integer); unbounded
 * (the live sheet uses call positions well above 6).
 */
export function parseAttendingCell(cell) {
  const raw = norm(cell);
  const isAIC = /\baic\b/i.test(raw);
  const stripped = raw.replace(/\baic\b/i, " ").trim();
  const code = (stripped.match(/[A-Za-z]+/) || [null])[0];
  // Post-call slot: "(PC3)" / "PC3" — takes priority over a plain call number
  // (so "WIL (PC6)" is PC6, NOT call order 6).
  const pcMatch = stripped.match(/\bPC\s*(\d+)/i);
  const postCall = pcMatch ? Number(pcMatch[1]) : null;
  let callOrder = null;
  if (postCall == null) {
    const numMatch = stripped.match(/\((\d+)/) || stripped.match(/\b(\d+)\b/);
    callOrder = numMatch ? Number(numMatch[1]) : null;
  }
  return { code: code ? code.toUpperCase() : null, callOrder, postCall, isAIC };
}

// ─── The provider (AHP) cell ────────────────────────────────────────────────

// am-pm range like "9a-7p" / "11p-7a" — the second time is when the CRNA
// leaves, so its hour is the shift NUMBER ("9a-7p" → 7).
const SHIFT_RANGE_RE = /^\d{1,2}[ap]-(\d{1,2})[ap]$/i;
const SHIFT_LSUFFIX_RE = /^(\d+)L$/i;

// The cross-room routing annotation ("to CT", "from IR", "to/from CIE6") now
// lives in domain/personSegment as `stripRoutingNote`, because the ORIENT path
// has to judge a name with the journey taken off it and a second spelling of
// "what an annotation looks like" is how those two drifted apart.

/** The leaving HOUR a shift token implies, or null when it implies none. */
function shiftNumberOf(token) {
  const t = norm(token);
  const range = t.match(SHIFT_RANGE_RE);
  if (range) return Number(range[1]);
  const lsuf = t.match(SHIFT_LSUFFIX_RE);
  if (lsuf) return Number(lsuf[1]);
  if (/^\d{1,2}$/.test(t)) return Number(t);
  // "7A", "ASAP", "@9" — a real code with no fixed clock end.
  return null;
}

/**
 * ONE person's segment → their name, their OWN shift code, and their arrows.
 *
 * ⚠️ `shiftCode` IS THE RAW TOKEN AND IT IS NOT REDUNDANT. `shiftNumber`
 * flattens "7p-7a" to 7, and 7 read back through `shiftCodeEndHour24` is 7 PM
 * — twelve hours wrong for a night shift, against a real person, on a board
 * that decides who is covering a room. Everything storing a departure stores
 * the CODE.
 */
function readOnePerson(segment, role) {
  const notes = [];
  let toRoom = "";
  let fromRoom = "";
  let body = norm(segment);

  const routed = stripRoutingNote(body);
  if (routed.text !== body) {
    toRoom = routed.toRoom;
    fromRoom = routed.fromRoom;
    body = routed.text;
  } else {
    // Bare trailing room token AFTER a shift digit ("… 3 TOR16", "… 5 WC3").
    const bare = body.match(/(\d)\s+([A-Za-z]{2,6}\s?\d{1,2})\s*$/);
    if (bare) {
      toRoom = bare[2].trim().toUpperCase();
      body = body.slice(0, bare.index + 1);
    }
  }

  // A clock this app cannot interpret must never become a departure, and it
  // must never be chewed into the name either ("Lester 7-8:" / shift "30").
  body = stripTimeNote(body.trim());

  // "Moore, Shannon 5" is Shannon Moore. Done AFTER the routing strip so the
  // rule only ever sees a name and a shift, and recorded as a note because a
  // reordered name is a change a human should be able to see.
  const flipped = flipSurnameFirst(body);
  if (flipped !== body) {
    notes.push({ code: "surnameFirst", text: body });
    body = flipped;
  }

  // The annotation / block-suffix / spaced-window normalisation — the reason a
  // cell no longer has to END in a shift token to keep its shift.
  const nz = normalizeSegment(body);
  notes.push(...nz.notes);
  body = nz.text;

  let name = body;
  let shiftCode = null;
  let shiftNumber = null;
  const tokens = body.split(/\s+/).filter(Boolean);
  const last = tokens[tokens.length - 1];
  if (tokens.length > 1 && isShiftToken(last)) {
    name = tokens.slice(0, -1).join(" ");
    shiftCode = last;
    shiftNumber = shiftNumberOf(last);
  } else {
    // "Name-5" — the documented hyphen spelling, which is not a token break.
    const m = body.match(/^(.*?)[\s-]+(\d{1,2})\s*$/);
    if (m && m[1].trim()) {
      name = m[1].trim();
      shiftCode = m[2];
      shiftNumber = Number(m[2]);
    }
  }

  return {
    name,
    shiftCode,
    shiftNumber,
    toRoom,
    fromRoom,
    role,
    notes,
    // A name this reader could not vouch for. It is still EMITTED — a blank
    // where a name belongs is the one outcome worse than an odd one — but it
    // is flagged so nothing downstream creates a person out of it.
    issue: name ? personSegmentIssue(name, { minWords: 1 }) : { code: "empty" },
  };
}

/**
 * ⚠️ THE SINGLE DECISION: how many people is this cell?
 *
 * Asked once, in one place, for all four spellings, and it fails closed.
 * Returns the RAW segments plus everything it understood and everything it did
 * not.
 */
function splitCell(raw) {
  const notes = [];
  const review = [];

  // 1. ORIENT — asked on the WHOLE cell first, because the sheet writes
  //    "orients w/ Sam Gongora" and that "w/" carries a slash.
  const orient = orientationParts(raw);
  if (orient && !orient.issue) {
    return { segments: [orient.preceptor, orient.orientee], roles: ["seat", "orientee"], notes, review };
  }
  let body = raw;
  if (orient && orient.issue) {
    // ⚠️ THE `to OB floor` CASE. "Olivia Meitzner 3 Orient to OB floor" is ONE
    // person and a routing note. Keeping the tail on the name would lose her
    // shift and her id; splitting on it invents a colleague. So: drop the tail
    // to a note, keep the person, and tell a human what was dropped.
    notes.push({ code: "orientNote", text: orient.tail });
    review.push({
      code: "orientNotAPair",
      text: raw,
      detail: `"${orient.orientee}" is not a person (${orient.issue.code})`,
    });
    body = orient.preceptor;
  }

  // 2. COMMA — gated, unlike before. "Sarabel Mercado, Torres 3" is one human
  //    with a stray comma; " Jenny Lebron 3, Renee Mayr 7" is two.
  const commaParts = body.split(",").map((p) => p.trim()).filter(Boolean);
  let parts;
  // ⚠️ ASKED BEFORE THE SPLIT, SO A CELL THIS READER UNDERSTANDS IS NOT
  // REPORTED AS A REFUSAL. "Moore, Shannon 5" is surname-first — one person —
  // and the comma gate correctly declined to split it, but then filed a
  // `commaNotAllPeople` review item against a row that came out perfectly.
  // A review list that cries wolf is a review list nobody reads.
  if (flipSurnameFirst(body) !== body) {
    parts = [body];
  } else if (commaParts.length > 1) {
    const named = commaParts.filter((p) => !isEmptySegment(p));
    if (named.length && named.every((p) => isPersonSegment(p))) {
      parts = named;
    } else {
      review.push({ code: "commaNotAllPeople", text: body, detail: "left whole" });
      parts = [body];
    }
  } else {
    parts = [body];
  }

  // 3. SLASH / TRAILING PAREN, per comma part.
  const segments = [];
  for (const part of parts) {
    const inner = orientationParts(part);
    if (inner && !inner.issue) {
      segments.push(inner.preceptor, inner.orientee);
      continue;
    }
    const co = coProviderSegments(part);
    if (co) {
      segments.push(...co);
      continue;
    }
    const issue = coProviderIssue(part);
    if (issue) review.push({ code: issue.code, text: part, detail: issue.detail });
    segments.push(part);
  }

  const roles = segments.map((_, i) => (i === 0 ? "seat" : "colleague"));
  return { segments, roles, notes, review };
}

/**
 * One AHP cell → the PEOPLE it names, what else it said, and what it could not
 * be read as.
 *
 * @returns {{
 *   raw: string,
 *   people: Array<{name, shiftCode, shiftNumber, toRoom, fromRoom, role, notes, issue}>,
 *   notes: Array<{code, text}>,
 *   review: Array<{code, text, detail}>,
 *   understood: boolean
 * }}
 *   role ∈ "seat" | "orientee" | "colleague".
 */
export function readProviderCell(cell) {
  const raw = norm(cell);
  if (!raw) return { raw, people: [], notes: [], review: [], understood: true };
  if (isEmptySegment(raw)) {
    return { raw, people: [], notes: [{ code: "placeholder", text: raw }], review: [], understood: true };
  }

  const { segments, roles, notes, review } = splitCell(raw);
  const people = [];
  segments.forEach((seg, i) => {
    if (isEmptySegment(seg)) return;
    const person = readOnePerson(seg, roles[i]);
    if (!person.name) return;
    if (person.issue) {
      review.push({
        code: "nameNotUnderstood",
        text: seg,
        detail: `"${person.name}" (${person.issue.code})`,
      });
    }
    notes.push(...person.notes);
    people.push(person);
  });

  return { raw, people, notes, review, understood: review.length === 0 };
}

const EMPTY_CRNA = {
  name: "",
  shiftNumber: null,
  shiftCode: null,
  toRoom: "",
  fromRoom: "",
  orienteeName: "",
  orienteeShiftNumber: null,
  orienteeShiftCode: null,
  orienteeShiftInherited: false,
};

/**
 * The seat holder of an AHP cell, in the shape the row builder expects.
 * A second person is carried here ONLY when they are an orientee; colleagues
 * listed alongside come back from `parseCrnaCellAll`.
 */
export function parseCrnaCell(cell) {
  const read = readProviderCell(cell);
  const seat = read.people[0];
  if (!seat) return { ...EMPTY_CRNA };
  const second = read.people[1];
  const orienting = second && second.role === "orientee" ? second : null;
  // "Rebecca Sigler 3 orient Brianna Michaud 7" — two people in one room who
  // leave at two different times. A stated shift is never overwritten.
  const statedOwnShift = Boolean(orienting && orienting.shiftCode != null);
  return {
    name: seat.name,
    shiftNumber: seat.shiftNumber,
    shiftCode: seat.shiftCode,
    toRoom: seat.toRoom,
    fromRoom: seat.fromRoom,
    orienteeName: orienting ? orienting.name : "",
    orienteeShiftNumber: orienting ? (statedOwnShift ? orienting.shiftNumber : seat.shiftNumber) : null,
    orienteeShiftCode: orienting ? (statedOwnShift ? orienting.shiftCode : seat.shiftCode) : null,
    // ⚠️ AN INHERITED SHIFT IS NOT A STATED ONE, AND THE DIFFERENCE IS A REAL
    // PERSON'S GOING-HOME TIME. Owner, 2026-09-07, on "…/Orient Cheryl Martin":
    // a trainee with no number of their own stands in that room for the
    // preceptor's shift, so that is the shift to use — but a surface that says
    // "leaves 3 PM" is quoting the sheet, and here the sheet never said it.
    // Carried so a screen can hedge instead of the board inventing a fact.
    orienteeShiftInherited: orienting ? !statedOwnShift : false,
    // ⚠️ "I read a name here and I cannot vouch for it." Non-null means the
    // string is still shown — a blank where a name belongs is worse — but
    // nothing downstream may CREATE a colleague out of it.
    issue: seat.issue || null,
    orienteeIssue: orienting ? orienting.issue || null : null,
  };
}

/** EVERY provider an AHP cell names, in the order written. */
export function parseCrnaCellAll(cell) {
  return readProviderCell(cell).people.map((p) => ({
    name: p.name,
    shiftNumber: p.shiftNumber,
    shiftCode: p.shiftCode,
    toRoom: p.toRoom,
    fromRoom: p.fromRoom,
    role: p.role,
    issue: p.issue || null,
    orienteeName: "",
    orienteeShiftNumber: null,
    orienteeShiftCode: null,
  }));
}

// OB (obstetrics) rooms run round-the-clock; the room doc carries the
// canonical location="ob" used everywhere else. Matched by room NAME here.
export const isObSheetRoom = (name) => /\bob\b/i.test(norm(name));
